import * as THREE from "three";
import { createOptimizedVenue } from "./optimizedVenue.js";
import { createOptimizedEvent } from "./optimizedEvent.js";

export function createWorld(container) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x9fc9dd);
  scene.fog = new THREE.Fog(0x9fc9dd, 118, 245);

  const camera = new THREE.PerspectiveCamera(
    50,
    container.clientWidth / container.clientHeight,
    0.1,
    500,
  );
  camera.position.set(104, 92, 118);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 1.5));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  container.appendChild(renderer.domElement);

  const hemisphere = new THREE.HemisphereLight(0xe0f4ff, 0x52624d, 2.2);
  scene.add(hemisphere);

  const sun = new THREE.DirectionalLight(0xffefd5, 3.25);
  sun.position.set(-52, 88, 48);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1536, 1536);
  sun.shadow.camera.left = -92;
  sun.shadow.camera.right = 92;
  sun.shadow.camera.top = 92;
  sun.shadow.camera.bottom = -92;
  sun.shadow.camera.near = 12;
  sun.shadow.camera.far = 210;
  sun.shadow.bias = -0.00035;
  scene.add(sun);

  const base = new THREE.Mesh(
    new THREE.PlaneGeometry(250, 250),
    new THREE.MeshStandardMaterial({ color: 0x879091, roughness: 1 }),
  );
  base.rotation.x = -Math.PI / 2;
  base.position.y = -0.035;
  base.receiveShadow = true;
  scene.add(base);

  const venue = createOptimizedVenue(renderer);
  const event = createOptimizedEvent();
  scene.add(venue, event.group);

  const nightLights = [];
  for (const [x, z, color] of [[0, 54, 0xffa04b], [-52, 3, 0xff7043], [47, 3, 0x70bde6], [8, 22, 0xffd46b]]) {
    const light = new THREE.PointLight(color, 0, 34, 2);
    light.position.set(x, 8, z);
    scene.add(light);
    nightLights.push(light);
  }

  function setNight(isNight) {
    scene.background.set(isNight ? 0x071422 : 0x9fc9dd);
    scene.fog.color.copy(scene.background);
    hemisphere.intensity = isNight ? 0.48 : 2.2;
    sun.intensity = isNight ? 0.5 : 3.25;
    renderer.toneMappingExposure = isNight ? 0.8 : 1.08;
    nightLights.forEach(light => { light.intensity = isNight ? 24 : 0; });
  }

  function resize() {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  }

  function getDiagnostics() {
    let objects = 0;
    let meshes = 0;
    let instancedMeshes = 0;
    const geometries = new Set();
    const materials = new Set();
    scene.traverse(object => {
      objects += 1;
      if (object.isMesh) {
        meshes += 1;
        if (object.isInstancedMesh) instancedMeshes += 1;
        if (object.geometry) geometries.add(object.geometry.uuid);
        const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
        objectMaterials.filter(Boolean).forEach(material => materials.add(material.uuid));
      }
    });
    return {
      objects,
      meshes,
      instancedMeshes,
      geometries: geometries.size,
      materials: materials.size,
      renderer: {
        calls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        points: renderer.info.render.points,
        lines: renderer.info.render.lines,
      },
    };
  }

  Object.defineProperty(globalThis, "__festival3dDiagnostics", {
    value: getDiagnostics,
    configurable: true,
  });

  return {
    scene,
    camera,
    renderer,
    hotspots: event.hotspots,
    setNight,
    resize,
    getDiagnostics,
  };
}
